export interface ClientErrorReportingConfig {
  apiBase?: string
  endpointPath?: string
  sourceApp?: string
  token?: string | null
}

export interface ClientErrorEventContext {
  feature?: unknown
  action?: unknown
  endpoint?: unknown
  method?: unknown
  status?: unknown
  errorKey?: unknown
  page?: unknown
  component?: unknown
  componentStack?: unknown
  requestId?: unknown
  publicationId?: unknown
  smartSpotEditId?: unknown
  transportId?: unknown
  messageId?: unknown
  prefillSourceType?: unknown
  prefillSourceId?: unknown
  selectedExchanges?: unknown
  validationField?: unknown
  validationReason?: unknown
}

const CORRELATION_ID_STORAGE_KEY = 'cargonice-correlation-id'
const ERROR_EVENT_QUEUE_KEY = 'cargonice-client-error-event-queue'
const INCIDENT_REF_PATTERN = /^ERR-[A-F0-9]{12}$/
const MAX_QUEUED_EVENTS = 20
const reportedObjects = new WeakSet<object>()

let reportingConfig: Required<Pick<ClientErrorReportingConfig, 'apiBase' | 'endpointPath' | 'sourceApp'>> & {
  token?: string | null
} = {
  apiBase: '',
  endpointPath: '/api/diagnostics/client-error-events',
  sourceApp: 'frontend',
  token: null,
}

export const configureClientErrorReporting = (config: ClientErrorReportingConfig) => {
  reportingConfig = {
    ...reportingConfig,
    ...(config.apiBase !== undefined ? { apiBase: config.apiBase } : {}),
    ...(config.endpointPath !== undefined ? { endpointPath: config.endpointPath } : {}),
    ...(config.sourceApp !== undefined ? { sourceApp: config.sourceApp } : {}),
    ...(config.token !== undefined ? { token: config.token } : {}),
  }
  flushQueuedClientErrorEvents()
}

export const reportClientErrorEvent = (
  error: unknown,
  context: ClientErrorEventContext = {},
  overrides: Record<string, unknown> = {}
) => {
  if (error && typeof error === 'object') {
    if (reportedObjects.has(error)) {
      return normalizeIncidentRef(overrides.incidentRef)
    }
    reportedObjects.add(error)
  }

  const payload = buildPayload(error, context, overrides)
  if (!payload.message && !payload.errorKey) {
    return normalizeIncidentRef(payload.incidentRef)
  }

  if (!reportingConfig.token || typeof fetch !== 'function') {
    enqueuePayload(payload)
    return normalizeIncidentRef(payload.incidentRef)
  }

  flushQueuedClientErrorEvents()
  void sendPayload(payload, false)
  return normalizeIncidentRef(payload.incidentRef)
}

export const createClientIncidentRef = () => {
  const bytes = new Uint8Array(6)
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes)
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256)
    }
  }
  return `ERR-${Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`
}

export const flushQueuedClientErrorEvents = () => {
  if (!reportingConfig.token || typeof fetch !== 'function') {
    return
  }
  const queued = readQueue()
  if (!queued.length) {
    return
  }
  writeQueue([])
  for (const payload of queued) {
    void sendPayload(payload, true)
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    flushQueuedClientErrorEvents()
  })
}

const sendPayload = async (payload: Partial<ClientErrorPayload>, fromQueue: boolean) => {
  try {
    const response = await fetch(`${reportingConfig.apiBase}${reportingConfig.endpointPath}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${reportingConfig.token}`,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    })
    if (!response.ok) {
      if (shouldQueueStatus(response.status)) {
        enqueuePayload(payload)
      }
      emitDeliveryFailure(payload, response.status)
      return
    }
  } catch (error) {
    enqueuePayload(payload)
    emitDeliveryFailure(payload, undefined, error)
    return
  }
  if (!fromQueue) {
    flushQueuedClientErrorEvents()
  }
}

const buildPayload = (
  error: unknown,
  context: ClientErrorEventContext,
  overrides: Record<string, unknown>
) => {
  const errorObject = error && typeof error === 'object' ? (error as Record<string, unknown>) : null
  const incidentRef =
    normalizeIncidentRef(overrides.incidentRef) ||
    normalizeIncidentRef(errorObject?.__incidentRef) ||
    createClientIncidentRef()
  const status = normalizeNumber(overrides.httpStatus ?? context.status ?? errorObject?.status)
  const errorKey = normalizeText(overrides.errorKey ?? context.errorKey ?? readPayloadErrorKey(errorObject))
  const responseDetails = readPayloadDetails(errorObject)
  const endpoint = normalizeText(overrides.path ?? context.endpoint ?? readUrlPath(errorObject?.url))
  const method = normalizeText(overrides.method ?? context.method)
  const message =
    normalizeText(overrides.message) ||
    normalizeText(errorObject?.message) ||
    normalizeText(error) ||
    errorKey ||
    'Client error'
  const publicationId = normalizeNumber(overrides.publicationId ?? context.publicationId)
  const transportId = normalizeText(context.transportId)
  const entityType =
    normalizeText(overrides.entityType) ||
    (publicationId != null ? 'publication' : transportId ? 'ai_email_transport' : undefined)
  const entityId =
    normalizeText(overrides.entityId) ||
    (publicationId != null ? String(publicationId) : transportId)

  return pruneObject({
    incidentRef,
    severity: normalizeText(overrides.severity) || (status != null && status >= 400 && status < 500 ? 'WARN' : 'ERROR'),
    sourceApp: reportingConfig.sourceApp,
    category: normalizeText(overrides.category) || resolveCategory(context, status),
    message,
    errorKey,
    httpStatus: status,
    method: method?.toUpperCase(),
    path: endpoint,
    correlationId: getCorrelationIdRef(),
    requestId: normalizeText(overrides.requestId ?? context.requestId),
    entityType,
    entityId,
    exchangeKey: normalizeText(overrides.exchangeKey),
    publicationId,
    stackTraceShort: normalizeText(overrides.stackTraceShort ?? errorObject?.stack ?? context.componentStack),
    details: pruneObject({
      feature: normalizeText(context.feature),
      action: normalizeText(context.action),
      page: normalizeText(context.page) || (typeof window !== 'undefined' ? window.location.pathname : undefined),
      component: normalizeText(context.component),
      endpoint,
      method: method?.toUpperCase(),
      status,
      errorKey,
      requestId: normalizeText(context.requestId),
      publicationId,
      smartSpotEditId: normalizeText(context.smartSpotEditId),
      transportId,
      messageId: normalizeText(context.messageId),
      selectedExchanges: normalizeStringArray(context.selectedExchanges),
      validationField: normalizeText(overrides.validationField ?? context.validationField ?? responseDetails?.validationField),
      validationReason: normalizeText(overrides.validationReason ?? context.validationReason ?? responseDetails?.validationReason),
    }),
  })
}

interface ClientErrorPayload {
  incidentRef?: string
  severity?: string
  sourceApp?: string
  category?: string
  message?: string
  errorKey?: string
  httpStatus?: number
  method?: string
  path?: string
  correlationId?: string
  requestId?: string
  entityType?: string
  entityId?: string
  exchangeKey?: string
  publicationId?: number
  stackTraceShort?: string
  details?: Record<string, unknown>
}

const shouldQueueStatus = (status: number) => status === 401 || status === 408 || status === 429 || status >= 500

const normalizeIncidentRef = (value: unknown): string | undefined => {
  const text = normalizeText(value)?.toUpperCase()
  return text && INCIDENT_REF_PATTERN.test(text) ? text : undefined
}

const readQueue = (): Partial<ClientErrorPayload>[] => {
  if (typeof window === 'undefined') {
    return []
  }
  try {
    const raw = window.localStorage.getItem(ERROR_EVENT_QUEUE_KEY)
    if (!raw) {
      return []
    }
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((entry) => entry && typeof entry === 'object') : []
  } catch {
    return []
  }
}

const writeQueue = (queue: Partial<ClientErrorPayload>[]) => {
  if (typeof window === 'undefined') {
    return
  }
  try {
    if (!queue.length) {
      window.localStorage.removeItem(ERROR_EVENT_QUEUE_KEY)
      return
    }
    window.localStorage.setItem(ERROR_EVENT_QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUED_EVENTS)))
  } catch {
    // Ignore quota/security failures; diagnostics must not break user flows.
  }
}

const enqueuePayload = (payload: Partial<ClientErrorPayload>) => {
  if (!payload || !normalizeIncidentRef(payload.incidentRef)) {
    return
  }
  const queue = readQueue().filter((entry) => entry.incidentRef !== payload.incidentRef)
  queue.push(payload)
  writeQueue(queue)
}

const emitDeliveryFailure = (payload: Partial<ClientErrorPayload>, status?: number, error?: unknown) => {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.dispatchEvent(
      new CustomEvent('cargonice-client-error-delivery-failed', {
        detail: pruneObject({
          incidentRef: normalizeIncidentRef(payload.incidentRef),
          status,
          endpointPath: reportingConfig.endpointPath,
          errorName: error && typeof error === 'object' ? normalizeText((error as Record<string, unknown>).name) : undefined,
        }),
      })
    )
  } catch {
    // Ignore browser event failures.
  }
}

const resolveCategory = (context: ClientErrorEventContext, status?: number) => {
  if (normalizeText(context.action) === 'render-crash') {
    return 'frontend_render_error'
  }
  if (normalizeText(context.action) === 'unhandledrejection') {
    return 'frontend_unhandled_rejection'
  }
  if (normalizeText(context.endpoint) || status != null) {
    return 'frontend_api_error'
  }
  return 'frontend_error'
}

const readPayloadErrorKey = (error: Record<string, unknown> | null) => {
  const payload = error?.payload
  if (!payload || typeof payload !== 'object') {
    return undefined
  }
  const payloadObject = payload as Record<string, unknown>
  return payloadObject.error ?? payloadObject.message
}

const readPayloadDetails = (error: Record<string, unknown> | null): Record<string, unknown> | undefined => {
  const payload = error?.payload
  if (!payload || typeof payload !== 'object') {
    return undefined
  }
  const details = (payload as Record<string, unknown>).details
  return details && typeof details === 'object' && !Array.isArray(details)
    ? (details as Record<string, unknown>)
    : undefined
}

const readUrlPath = (url: unknown) => {
  const text = normalizeText(url)
  if (!text) {
    return undefined
  }
  try {
    return new URL(text, typeof window !== 'undefined' ? window.location.origin : 'http://localhost').pathname
  } catch {
    return text
  }
}

const getCorrelationIdRef = () => {
  if (typeof window === 'undefined') {
    return undefined
  }
  try {
    return normalizeText(window.sessionStorage.getItem(CORRELATION_ID_STORAGE_KEY))
  } catch {
    return undefined
  }
}

const normalizeText = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed ? trimmed : undefined
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }
  return undefined
}

const normalizeNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

const normalizeStringArray = (value: unknown): string[] | undefined => {
  const rawValues = Array.isArray(value) ? value : value != null ? [value] : []
  const values = rawValues
    .map((entry) => normalizeText(entry))
    .filter((entry): entry is string => Boolean(entry))
  return values.length ? values : undefined
}

const pruneObject = <T extends Record<string, unknown>>(
  value: T
): { [K in keyof T]?: Exclude<T[K], undefined> } => {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => {
      if (entry == null) {
        return false
      }
      if (typeof entry === 'string') {
        return entry.trim() !== ''
      }
      if (Array.isArray(entry)) {
        return entry.length > 0
      }
      if (typeof entry === 'object') {
        return Object.keys(entry).length > 0
      }
      return true
    })
  ) as { [K in keyof T]?: Exclude<T[K], undefined> }
}
